import React, { useEffect, useMemo, useCallback } from 'react';
import { Chat, IOStatusEnum } from '@matap/shared';
import styled from 'styled-components/native';
import { useFileAsset } from '@/hooks/use.file.asset';
import { useScreenSize } from '@/hooks/use.screen.size';
import { Box } from '@/components/base/box';
import { ChatFooterView } from './chat.footer.view';
import { DownloadUploadStateView } from './download.upload.state.view';
import { ChatContainer } from './chat.container';
import { AppCardView } from '@/components/base/app.card.view';
import { Pressable, Platform } from 'react-native';
import { AppIconButton } from '@/components/base/app.icon.button';
import { AppImageView } from '@/components/base/app.image.view';
import * as VideoThumbnails from 'expo-video-thumbnails';
import { generateThumbnailFromBlobUrl } from '../../../utils/media.utils';
import { hpVal, scaleImage } from '../../../utils';
import { useAppRouter } from '@/hooks/use.app.router';
import { ModalNamesEnum } from '@/constants/enums/modal.names.enum';

interface Props {
  chat: Chat;
  isSentByUser: boolean;
  temporary: boolean;
  onLongPress: (e: any) => void;
}

function BaseChatVideoView({ chat, isSentByUser, temporary, onLongPress }: Props) {
  const { state, mediaMetaData } = useFileAsset(chat, temporary);
  const { wp, hp } = useScreenSize();
  const { navigateTo } = useAppRouter();

  const [thumbnailUri, setThumbnailUri] = React.useState<string | null>(state?.source.thumbnail || null);

  const { width, height } = useMemo(
    () => scaleImage(mediaMetaData?.width, mediaMetaData.height, Math.min(wp(70), 300), hp(50)),
    [hp, mediaMetaData.height, mediaMetaData.width, wp],
  );

  // 1. Generate thumbnail when video is downloaded
  useEffect(() => {
    if (!state?.localAddress || thumbnailUri) return;
    let isMounted = true;

    if (state.source.thumbnail) {
      setThumbnailUri(state.source.thumbnail);
    } else {
      const generateThumbnail = async () => {
        try {
          let uri: string;
          if (Platform.OS === 'web') {
            // eslint-disable-next-line react-hooks/immutability
            uri = await generateThumbnailFromBlobUrl(state.localAddress, width, height, 1, 0.5);
          } else {
            uri = (
              await VideoThumbnails.getThumbnailAsync(state.localAddress, {
                time: 1000,
                quality: 0.5,
              })
            ).uri;
          }
          state.source.thumbnail = uri;
          if (isMounted) setThumbnailUri(uri);
        } catch (e) {
          console.warn('Failed to generate thumbnail', e);
        }
      };
      generateThumbnail();
    }

    return () => {
      isMounted = false;
    };
  }, [state?.localAddress, thumbnailUri, state?.source, width, height]);

  const handlePress = useCallback(() => {
    if (state?.localAddress) {
      navigateTo(ModalNamesEnum.VideoPlayer, { uri: state?.localAddress });
    }
  }, [state?.localAddress, navigateTo]);

  return (
    <ChatContainer isSentByUser={isSentByUser}>
      <Container width={width} height={height} shadowColor={'#000'} shadowOpacity={0.2} shadowOffsetX={0} shadowOffsetY={2} blurRadius={8}>
        {state?.localAddress ? <VideoThumbnailView onPress={handlePress} onLongPress={onLongPress} thumbnailUrl={thumbnailUri} /> : <Placeholder />}

        {state && state.state !== IOStatusEnum.READY && (
          <IOStatusContainer pointerEvents={'box-none'}>
            <IOStatusView fileState={state} chatId={chat._id} />
          </IOStatusContainer>
        )}
      </Container>
      <ChatFooterView chat={chat} isSentByUser={isSentByUser} />
    </ChatContainer>
  );
}

// 3. Render only the lightweight thumbnail and play icon overlay
function VideoThumbnailView({ onPress, onLongPress, thumbnailUrl }: { onPress: () => void; onLongPress: (e: any) => void; thumbnailUrl?: string | null }) {
  return (
    <ThumbnailPressable onPress={onPress} onLongPress={onLongPress} delayLongPress={400}>
      {thumbnailUrl && <ThumbnailImage source={thumbnailUrl} contentFit="cover" />}

      <ControlsOverlay pointerEvents={'none'}>
        <PlayButtonOverlay>
          <AppIconButton icon={'play'} size={'medium'} color={'#fff'} />
        </PlayButtonOverlay>
      </ControlsOverlay>
    </ThumbnailPressable>
  );
}

const Container = styled(AppCardView)<{ width: number; height: number }>`
  width: ${({ width }) => width}px;
  height: ${({ height }) => height}px;
  border-radius: ${hpVal(1)};
  overflow: hidden;
  background-color: transparent;
`;

const Placeholder = styled(Box)`
  flex: 1;
  background-color: #000;
`;

const IOStatusContainer = styled(Box)`
  position: absolute;
  align-items: center;
  justify-content: center;
  right: 0;
  left: 0;
  bottom: 0;
  top: 0;
`;

const IOStatusView = styled(DownloadUploadStateView)``;

const ControlsOverlay = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  justify-content: center;
  align-items: center;
`;

const ThumbnailPressable = styled(Pressable)`
  flex: 1;
  background-color: #000;
`;

const ThumbnailImage = styled(AppImageView)`
  flex: 1;
  width: 100%;
  height: 100%;
`;

const PlayButtonOverlay = styled(Box)`
  width: 50px;
  height: 50px;
  border-radius: 25px;
  background-color: rgba(0, 0, 0, 0.4);
  justify-content: center;
  align-items: center;
`;

export const ChatVideoView = React.memo(BaseChatVideoView);
